import { databases, DATABASE_ID, COLLECTION_ID_PURCHASE_ORDERS } from '../config/appwrite';
import { ID, Query } from 'appwrite';
import { correctPoItems } from '../utils/poItemNameNormalize';

/**
 * Appwrite `items` is sometimes a JSON array, sometimes a JSON string that
 * itself contains a stringified array (double-encoded). Unwrap until we get
 * a real array of line items.
 */
export const parsePoItemsField = (raw) => {
  let value = raw;
  for (let i = 0; i < 4; i += 1) {
    if (Array.isArray(value)) return value;
    if (value == null || value === '') return [];
    if (typeof value !== 'string') return [];
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  return Array.isArray(value) ? value : [];
};

/** Parse TZS strings or numeric PO / invoice amounts. */
export const parsePoCurrency = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/TZS\s?/gi, '').replace(/,/g, '').trim();
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

export const isInvoicePaidStatus = (status) =>
  String(status || '').trim().toLowerCase() === 'paid';

/** PO cap = totalAmount when set, otherwise poAmount + VAT. */
export const getPoGrandTotal = (poDoc) => {
  const rawTotal = parsePoCurrency(poDoc?.totalAmount);
  const rawPoAmount = parsePoCurrency(poDoc?.poAmount);
  const rawVat = parsePoCurrency(poDoc?.vat);
  if (rawTotal > 0) return rawTotal;
  return rawPoAmount + rawVat;
};

/** Booked invoice total (amount + VAT), regardless of payment status. */
export const getInvoiceBookedTotal = (inv) => {
  const total = parsePoCurrency(inv?.totalAmount);
  if (total > 0) return total;
  return parsePoCurrency(inv?.amount) + parsePoCurrency(inv?.vat);
};

/**
 * PO balance = (PO amount + VAT) − sum of all invoice totals (any status).
 * invoicedAmount = sum of invoice amounts (ex VAT) for display.
 */
export const computePoInvoicingTotals = (poDoc, invoicesForPo = []) => {
  const poGrandTotal = getPoGrandTotal(poDoc);

  let totalInvoicedExVat = 0;
  let totalInvoicedAll = 0;
  invoicesForPo.forEach((inv) => {
    const exVat = parsePoCurrency(inv.amount);
    const booked = getInvoiceBookedTotal(inv);
    totalInvoicedExVat += exVat > 0 ? exVat : booked;
    totalInvoicedAll += booked;
  });

  return {
    poTotal: poGrandTotal,
    totalInvoiced: totalInvoicedExVat,
    totalInvoicedWithVat: totalInvoicedAll,
    poBalance: poGrandTotal - totalInvoicedAll
  };
};

/** Invoices linked to one PO document (matches LOT when PO number is shared). */
export const filterInvoicesForPoDoc = (poDoc, invoices = [], poDocsWithSameNumber = []) => {
  const poNumber = String(poDoc?.poNumber || '').trim();
  if (!poNumber) return [];

  const forNumber = (invoices || []).filter(
    (inv) => String(inv.poId || inv.poNumber || '').trim() === poNumber
  );

  const siblings = (poDocsWithSameNumber || []).filter(
    (d) => String(d.poNumber || '').trim() === poNumber
  );
  if (siblings.length <= 1) return forNumber;

  const poLot = String(poDoc?.lot || '').trim();
  const poSub = String(poDoc?.subcategory || '').trim().toLowerCase();

  return forNumber.filter((inv) => {
    const invLot = String(inv.lot || '').trim();
    if (poLot && invLot) return poLot === invLot;
    const invSub = String(inv.subcategory || '').trim().toLowerCase();
    if (poSub && invSub) return poSub === invSub;
    return false;
  });
};

/**
 * Purchase Orders Service
 * Handles all database operations for Purchase Orders
 */

// Get all purchase orders (auto-paginates so we never silently truncate the list)
export const getAllPurchaseOrders = async () => {
  try {
    const PAGE_SIZE = 100; // Appwrite hard cap per request
    const HARD_SAFETY_CAP = 10000; // keeps a runaway loop from hammering the API
    const allDocs = [];
    let offset = 0;
    let total = Infinity;

    while (offset < total && allDocs.length < HARD_SAFETY_CAP) {
      const response = await databases.listDocuments(
        DATABASE_ID,
        COLLECTION_ID_PURCHASE_ORDERS,
        [
          Query.orderDesc('poDate'),
          Query.limit(PAGE_SIZE),
          Query.offset(offset)
        ]
      );

      if (typeof response.total === 'number') {
        total = response.total;
      } else if (typeof response.totalCount === 'number') {
        total = response.totalCount;
      }

      if (!response.documents || response.documents.length === 0) break;

      allDocs.push(...response.documents);
      offset += response.documents.length;

      // Defensive break in case the server returns fewer docs than requested before reaching `total`.
      if (response.documents.length < PAGE_SIZE) break;
    }

    return allDocs.map(formatPurchaseOrder);
  } catch (error) {
    console.error('Error fetching purchase orders:', error);
    throw error;
  }
};

/** Total PO documents in the collection (Appwrite `total` — same scope as Dashboard). */
export const getPurchaseOrdersTotalCount = async () => {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTION_ID_PURCHASE_ORDERS,
    [Query.limit(1)]
  );
  if (typeof response.total === 'number') return response.total;
  if (typeof response.totalCount === 'number') return response.totalCount;
  return 0;
};

/**
 * Counts and totals aligned with Dashboard: DB total count + sum of (poAmount if &gt; 0 else totalAmount)
 * over fetched POs (up to 1000). Amount sum is complete when totalCount ≤ 1000.
 */
export const getPurchaseOrdersSummaryForStats = async () => {
  const [countResponse, pos] = await Promise.all([
    databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_PURCHASE_ORDERS,
      [Query.limit(1)]
    ),
    getAllPurchaseOrders()
  ]);
  const totalInDb =
    typeof countResponse.total === 'number'
      ? countResponse.total
      : (typeof countResponse.totalCount === 'number' ? countResponse.totalCount : pos.length);
  let totalAmount = 0;
  for (const po of pos) {
    const r = po._raw || {};
    const rawPo = Number(r.poAmount) || 0;
    const rawTotal = Number(r.totalAmount) || 0;
    totalAmount += rawPo > 0 ? rawPo : rawTotal;
  }
  return {
    totalCount: totalInDb,
    fetchedCount: pos.length,
    totalAmount
  };
};

// Get purchase orders with pagination (optionally filter by category)
export const getPurchaseOrdersByCategory = async (category = null, limit = 10, offset = 0) => {
  try {
    const queries = [
      Query.orderDesc('poDate'),
      Query.limit(limit),
      Query.offset(offset)
    ];
    
    // Only add category filter if category is provided
    if (category) {
      queries.unshift(Query.equal('category', category));
    }
    
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_PURCHASE_ORDERS,
      queries
    );
    
    return {
      documents: response.documents.map(formatPurchaseOrder),
      total: response.total
    };
  } catch (error) {
    console.error('Error fetching purchase orders:', error);
    throw error;
  }
};

// Get a single purchase order by ID
export const getPurchaseOrderById = async (id) => {
  try {
    const response = await databases.getDocument(
      DATABASE_ID,
      COLLECTION_ID_PURCHASE_ORDERS,
      id
    );
    return formatPurchaseOrder(response);
  } catch (error) {
    console.error('Error fetching purchase order:', error);
    throw error;
  }
};

// Get purchase order by PO number (first match when duplicates exist — prefer unique PO numbers)
export const getPurchaseOrderByPONumber = async (poNumber) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_PURCHASE_ORDERS,
      [
        Query.equal('poNumber', poNumber),
        Query.limit(1)
      ]
    );
    if (response.documents.length > 0) {
      return formatPurchaseOrder(response.documents[0]);
    }
    return null;
  } catch (error) {
    console.error('Error fetching purchase order by PO number:', error);
    throw error;
  }
};

/** True if a PO with this number already exists (optionally excluding one document id). */
export const checkPoNumberExists = async (poNumber, excludeDocumentId = null) => {
  const trimmed = String(poNumber || '').trim();
  if (!trimmed) return false;

  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTION_ID_PURCHASE_ORDERS,
    [Query.equal('poNumber', trimmed), Query.limit(5)]
  );

  if (!response.documents.length) return false;
  if (!excludeDocumentId) return true;

  return response.documents.some((doc) => doc.$id !== excludeDocumentId);
};

// Create a new purchase order
export const createPurchaseOrder = async (poData) => {
  try {
    const poNumber = String(poData.poNumber || '').trim();
    if (!poNumber) {
      throw new Error('PO number is required.');
    }

    const exists = await checkPoNumberExists(poNumber);
    if (exists) {
      throw new Error(`PO number "${poNumber}" already exists. Each PO must have a unique number.`);
    }

    const documentData = {
      lot: poData.lot || '',
      poNumber,
      poDate: poData.poDate || new Date().toISOString().split('T')[0],
      category: poData.category || 'material',
      subcategory: poData.subcategory || '',
      description: poData.description || '',
      supplierName: poData.supplierName || '',
      items: JSON.stringify(poData.items || []),
      poAmount: poData.poAmount || 0,
      vat: poData.vat || 0,
      totalAmount: poData.totalAmount || 0,
      invoicedAmount: poData.invoicedAmount || 0,
      poBalance: poData.poBalance || poData.totalAmount || 0,
      status: poData.status || 'pending'
    };

    const response = await databases.createDocument(
      DATABASE_ID,
      COLLECTION_ID_PURCHASE_ORDERS,
      ID.unique(),
      documentData
    );
    return formatPurchaseOrder(response);
  } catch (error) {
    console.error('Error creating purchase order:', error);
    throw error;
  }
};

// Update a purchase order
export const updatePurchaseOrder = async (id, poData) => {
  try {
    const documentData = {};
    
    if (poData.lot !== undefined) documentData.lot = poData.lot;
    if (poData.poNumber !== undefined) {
      const poNumber = String(poData.poNumber).trim();
      if (poNumber) {
        const exists = await checkPoNumberExists(poNumber, id);
        if (exists) {
          throw new Error(`PO number "${poNumber}" already exists. Each PO must have a unique number.`);
        }
        documentData.poNumber = poNumber;
      }
    }
    if (poData.poDate !== undefined) documentData.poDate = poData.poDate;
    if (poData.category !== undefined) documentData.category = poData.category;
    if (poData.subcategory !== undefined) documentData.subcategory = poData.subcategory;
    if (poData.description !== undefined) documentData.description = poData.description;
    if (poData.supplierName !== undefined) documentData.supplierName = poData.supplierName;
    if (poData.items !== undefined) documentData.items = JSON.stringify(poData.items);
    if (poData.poAmount !== undefined) documentData.poAmount = poData.poAmount;
    if (poData.vat !== undefined) documentData.vat = poData.vat;
    if (poData.totalAmount !== undefined) documentData.totalAmount = poData.totalAmount;
    if (poData.invoicedAmount !== undefined) {
      documentData.invoicedAmount = poData.invoicedAmount;
      if (poData.poBalance === undefined) {
        documentData.poBalance = getPoGrandTotal(poData) - parsePoCurrency(poData.invoicedAmount);
      }
    }
    if (poData.poBalance !== undefined) documentData.poBalance = poData.poBalance;
    if (poData.status !== undefined) documentData.status = poData.status;

    const response = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_PURCHASE_ORDERS,
      id,
      documentData
    );
    return formatPurchaseOrder(response);
  } catch (error) {
    console.error('Error updating purchase order:', error);
    throw error;
  }
};

// Delete a purchase order
export const deletePurchaseOrder = async (id) => {
  try {
    await databases.deleteDocument(
      DATABASE_ID,
      COLLECTION_ID_PURCHASE_ORDERS,
      id
    );
    return { success: true };
  } catch (error) {
    console.error('Error deleting purchase order:', error);
    throw error;
  }
};

// Format purchase order from Appwrite document format to app format
const formatPurchaseOrder = (doc) => {
  // Parse items if it's a string (supports double-encoded JSON from Appwrite)
  let items = [];
  try {
    items = parsePoItemsField(doc.items);
    items = correctPoItems(doc.lot, items);
  } catch (e) {
    items = [];
  }

  // Format date from ISO to dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    const num = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]+/g, '')) : (amount || 0);
    return `TZS ${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // PO balance stored in DB = (PO + VAT) − sum of all invoice totals (any status).
  const rawInvoiced = typeof doc.invoicedAmount === 'number' ? doc.invoicedAmount : parsePoCurrency(doc.invoicedAmount);
  const rawBalance =
    typeof doc.poBalance === 'number' ? doc.poBalance : parsePoCurrency(doc.poBalance);
  const derivedBalance = Number.isFinite(rawBalance)
    ? rawBalance
    : getPoGrandTotal(doc) - rawInvoiced;

  return {
    id: doc.$id,
    lot: doc.lot || '',
    poNumber: doc.poNumber || '',
    poDate: formatDate(doc.poDate),
    category: doc.category || 'material',
    subcategory: doc.subcategory || '',
    description: doc.description || '',
    supplierName: doc.supplierName || '',
    items: items,
    poAmount: formatCurrency(doc.poAmount || 0),
    vat: formatCurrency(doc.vat || 0),
    totalAmount: formatCurrency(doc.totalAmount || 0),
    invoicedAmount: formatCurrency(doc.invoicedAmount || 0),
    poBalance: formatCurrency(derivedBalance),
    status: doc.status || 'pending',
    _raw: {
      poAmount: doc.poAmount || 0,
      vat: doc.vat || 0,
      totalAmount: doc.totalAmount || 0,
      invoicedAmount: doc.invoicedAmount || 0,
      poBalance: derivedBalance
    }
  };
};

// Convert app format to database format (for create/update)
export const convertToDatabaseFormat = (poData) => {
  // Parse currency strings to numbers
  const parseCurrency = (currencyString) => {
    if (!currencyString) return 0;
    if (typeof currencyString === 'number') return currencyString;
    const cleaned = currencyString.toString().replace(/TZS\s?/gi, '').replace(/,/g, '').trim();
    return parseFloat(cleaned) || 0;
  };

  // Convert date from dd/mm/yyyy to ISO format
  const convertDate = (dateString) => {
    if (!dateString) return new Date().toISOString().split('T')[0];
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  return {
    lot: poData.lot || '',
    poNumber: poData.poNumber || '',
    poDate: convertDate(poData.poDate),
    category: poData.category || 'material',
    subcategory: poData.subcategory || '',
    description: poData.description || '',
    supplierName: poData.supplierName || '',
    items: JSON.stringify(poData.items || []),
    poAmount: parseCurrency(poData.poAmount),
    vat: parseCurrency(poData.vat),
    totalAmount: parseCurrency(poData.totalAmount),
    invoicedAmount: parseCurrency(poData.invoicedAmount),
    poBalance: parseCurrency(poData.poBalance) || (parseCurrency(poData.totalAmount) - parseCurrency(poData.invoicedAmount)),
    status: poData.status || 'pending'
  };
};

/** Recompute invoicedAmount + poBalance from invoice rows for one PO number (all LOT variants). */
export const syncPurchaseOrderInvoicing = async (poNumber, allInvoices = []) => {
  const trimmed = String(poNumber || '').trim();
  if (!trimmed) return null;

  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTION_ID_PURCHASE_ORDERS,
    [Query.equal('poNumber', trimmed), Query.limit(25)]
  );
  if (!response.documents.length) return null;

  const poDocs = response.documents;
  let lastUpdated = null;

  for (const doc of poDocs) {
    const poInvoices = filterInvoicesForPoDoc(doc, allInvoices, poDocs);
    const { poTotal, totalInvoiced, poBalance } = computePoInvoicingTotals(doc, poInvoices);

    const updated = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_PURCHASE_ORDERS,
      doc.$id,
      {
        invoicedAmount: totalInvoiced,
        poBalance,
        totalAmount: parsePoCurrency(doc.totalAmount) || poTotal
      }
    );
    lastUpdated = formatPurchaseOrder(updated);
  }

  return lastUpdated;
};

